/**
 * WorkflowEnvoisSection — Vague 4e (10.4) : les envois d'un parcours.
 *
 * Montée dans la fiche (FicheDevisSection) et dans le détail d'un projet.
 * Le cycle complet, sans jamais d'envoi silencieux :
 *  · « À préparer » : sujet/corps éditables, destinataires FINS choisis dans
 *    l'annuaire des utilisateurs, l'annuaire des tiers, ou saisis librement
 *    (email/téléphone) — puis Soumettre.
 *  · « À valider » : seuls les validateurs désignés (ou un admin) voient les
 *    boutons Valider (= faire partir) / Refuser (commentaire obligatoire).
 *  · Parti / échec / refusé : le résultat PAR destinataire reste lisible.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  api, ApiException,
  type WorkflowEnvoi, type EnvoiDestinataire, type AnnuaireDestinataires,
} from '@/lib/api';
import { Card, Button, Badge, Alert } from '@/components/ui';
import { Send, AlertCircle, Check, X, Plus } from 'lucide-react';

const CHAMP = 'h-9 w-full rounded-r-md border border-border bg-surface/60 px-2.5 text-sm text-ink focus:border-accent focus:outline-none';

const STATUTS: Record<WorkflowEnvoi['statut'], { l: string; v: 'neutral' | 'info' | 'warning' | 'success' | 'urgent' }> = {
  a_preparer: { l: 'À préparer', v: 'warning' },
  a_valider: { l: 'À valider', v: 'info' },
  envoye: { l: 'Parti', v: 'success' },
  echec: { l: 'Échec', v: 'urgent' },
  refuse: { l: 'Refusé', v: 'urgent' },
  annule: { l: 'Annulé', v: 'neutral' },
};

const CANAUX: Record<string, string> = { interne: 'Interne', email: 'Email', sms: 'SMS' };

export function WorkflowEnvoisSection({ instanceId }: { instanceId: string }) {
  const [envois, setEnvois] = useState<WorkflowEnvoi[]>([]);
  const [annuaire, setAnnuaire] = useState<AnnuaireDestinataires | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [edit, setEdit] = useState<string | null>(null);      // id de l'envoi ouvert
  const [fSujet, setFSujet] = useState('');
  const [fCorps, setFCorps] = useState('');
  const [fDests, setFDests] = useState<EnvoiDestinataire[]>([]);
  const [pick, setPick] = useState('');
  const [libre, setLibre] = useState('');
  const [refus, setRefus] = useState('');

  const charger = useCallback(async () => {
    try {
      const r = await api.workflows.envoisInstance(instanceId);
      setEnvois(r.envois);
    } catch { setEnvois([]); }
  }, [instanceId]);

  useEffect(() => { void charger(); }, [charger]);
  useEffect(() => {
    api.workflows.annuaireDestinataires().then(setAnnuaire).catch(() => setAnnuaire(null));
  }, []);

  if (envois.length === 0) return null;   // pas de flèche configurée : rien

  function ouvrirEdition(e: WorkflowEnvoi) {
    setEdit(e.id); setFSujet(e.sujet); setFCorps(e.corps);
    setFDests(e.destinataires); setPick(''); setLibre(''); setError(null); setMsg(null);
  }

  function ajouterPick() {
    if (!pick || !annuaire) return;
    const [type, id] = pick.split(':');
    const src = type === 'user'
      ? annuaire.users.find((u) => u.id === id)
      : annuaire.tiers.find((t) => t.id === id);
    if (!src || fDests.some((d) => d.id === id)) return;
    setFDests([...fDests, { type: type as 'user' | 'tiers', id, label: src.label }]);
    setPick('');
  }

  function ajouterLibre() {
    const v = libre.trim();
    if (!v || fDests.some((d) => d.valeur === v)) return;
    setFDests([...fDests, { type: 'libre', valeur: v, label: v }]);
    setLibre('');
  }

  async function agir(fn: () => Promise<WorkflowEnvoi>, ok: string) {
    setError(null); setMsg(null);
    try {
      await fn();
      setMsg(ok); setEdit(null); setRefus('');
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Action impossible');
    }
  }

  return (
    <Card variant="glass" padded>
      <h3 className="mb-2 flex items-center gap-1.5 text-sm font-medium text-ink">
        <Send className="h-4 w-4" aria-hidden="true" /> Envois du parcours
      </h3>
      {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
      {msg && <Alert variant="success" icon={Check}>{msg}</Alert>}

      <div className="space-y-2">
        {envois.map((e) => (
          <div key={e.id} className="rounded-r-lg border border-border/60 px-2.5 py-2">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
              <span className="font-medium text-ink">{e.sujet || 'Sans sujet'}</span>
              <Badge variant="neutral" size="sm">{CANAUX[e.canal]}</Badge>
              <Badge variant={STATUTS[e.statut].v} size="sm">{STATUTS[e.statut].l}</Badge>
              <span className="text-xs text-ink-muted">flèche « {e.flecheLibelle} »</span>
              {e.statut === 'a_preparer' && edit !== e.id && (
                <Button variant="secondary" size="sm" className="ml-auto" onClick={() => ouvrirEdition(e)}>
                  Préparer
                </Button>
              )}
            </div>

            {/* ── Préparation ─────────────────────────────────────────────── */}
            {edit === e.id && e.statut === 'a_preparer' && (
              <div className="mt-2 space-y-2 border-t border-border/60 pt-2">
                <input value={fSujet} onChange={(ev) => setFSujet(ev.target.value)}
                  placeholder="Sujet" aria-label="Sujet de l'envoi" className={CHAMP} />
                <textarea value={fCorps} onChange={(ev) => setFCorps(ev.target.value)}
                  placeholder="Message" aria-label="Corps de l'envoi" rows={4}
                  className="w-full rounded-r-md border border-border bg-surface/60 px-2.5 py-1.5 text-sm text-ink focus:border-accent focus:outline-none" />

                <div>
                  <p className="mb-1 text-xs text-ink-muted">
                    Destinataires — l'annuaire sert : utilisateurs, tiers, ou saisie libre
                    ({e.canal === 'sms' ? 'téléphone' : e.canal === 'email' ? 'email' : 'utilisateurs VPC seulement'}).
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {fDests.map((d, i) => (
                      <span key={i} className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-surface/60 px-2 py-0.5 text-xs text-ink">
                        {d.label}
                        <button type="button" aria-label={`Retirer ${d.label}`}
                          onClick={() => setFDests(fDests.filter((_, j) => j !== i))}
                          className="text-ink-muted hover:text-danger">
                          <X className="h-3 w-3" aria-hidden="true" />
                        </button>
                      </span>
                    ))}
                    {fDests.length === 0 && <span className="text-xs text-ink-muted">Aucun pour l'instant.</span>}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {annuaire && (
                      <>
                        <select value={pick} onChange={(ev) => setPick(ev.target.value)}
                          aria-label="Choisir dans l'annuaire"
                          className="h-8 rounded-r-md border border-border bg-surface/60 px-2 text-xs text-ink">
                          <option value="">— Annuaire —</option>
                          <optgroup label="Utilisateurs">
                            {annuaire.users.map((u) => (
                              <option key={u.id} value={`user:${u.id}`}>{u.label} ({u.role})</option>
                            ))}
                          </optgroup>
                          <optgroup label="Tiers">
                            {annuaire.tiers.map((t) => (
                              <option key={t.id} value={`tiers:${t.id}`}>{t.label}</option>
                            ))}
                          </optgroup>
                        </select>
                        <Button variant="ghost" size="sm" disabled={!pick} onClick={ajouterPick} aria-label="Ajouter ce destinataire">
                          <Plus className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </>
                    )}
                    {e.canal !== 'interne' && (
                      <>
                        <input value={libre} onChange={(ev) => setLibre(ev.target.value)}
                          placeholder={e.canal === 'sms' ? '06 12 34 56 78' : 'adresse@exemple.fr'}
                          aria-label="Destinataire libre"
                          className="h-8 rounded-r-md border border-border bg-surface/60 px-2 text-xs text-ink" />
                        <Button variant="ghost" size="sm" disabled={!libre.trim()} onClick={ajouterLibre} aria-label="Ajouter ce destinataire libre">
                          <Plus className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  <Button variant="primary" size="sm" disabled={fDests.length === 0}
                    onClick={() => void agir(async () => {
                      await api.workflows.majEnvoi(e.id, { sujet: fSujet, corps: fCorps, destinataires: fDests });
                      return api.workflows.soumettreEnvoi(e.id);
                    }, e.validationRequise
                      ? 'Envoi soumis — les validateurs désignés sont prévenus.'
                      : 'Envoi parti.')}>
                    {e.validationRequise ? 'Soumettre à validation' : 'Envoyer'}
                  </Button>
                  <Button variant="ghost" size="sm"
                    onClick={() => void agir(async () => {
                      return api.workflows.majEnvoi(e.id, { sujet: fSujet, corps: fCorps, destinataires: fDests });
                    }, 'Brouillon enregistré.')}>
                    Enregistrer le brouillon
                  </Button>
                  <Button variant="ghost" size="sm"
                    onClick={() => void agir(() => api.workflows.annulerEnvoi(e.id), 'Envoi annulé.')}>
                    Annuler l'envoi
                  </Button>
                </div>
              </div>
            )}

            {/* ── Validation ──────────────────────────────────────────────── */}
            {e.statut === 'a_valider' && (
              <div className="mt-2 border-t border-border/60 pt-2">
                <p className="text-xs text-ink-muted">
                  {e.corps ? `« ${e.corps.slice(0, 160)}${e.corps.length > 160 ? '…' : ''} » — ` : ''}
                  {e.destinataires.length} destinataire{e.destinataires.length > 1 ? 's' : ''} :{' '}
                  {e.destinataires.map((d) => d.label).join(', ')}
                </p>
                {e.peutValider ? (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <Button variant="primary" size="sm"
                      onClick={() => void agir(() => api.workflows.validerEnvoi(e.id), 'Envoi validé et parti.')}>
                      <Check className="h-4 w-4" aria-hidden="true" /> Valider et envoyer
                    </Button>
                    <input value={refus} onChange={(ev) => setRefus(ev.target.value)}
                      placeholder="Motif du refus…" aria-label="Motif du refus"
                      className="h-8 min-w-40 rounded-r-md border border-border bg-surface/60 px-2 text-xs text-ink" />
                    <Button variant="ghost" size="sm" disabled={!refus.trim()}
                      onClick={() => void agir(() => api.workflows.refuserEnvoi(e.id, refus), 'Envoi refusé — le préparateur est prévenu.')}>
                      Refuser
                    </Button>
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-ink-muted">
                    En attente d'un validateur désigné sur cette flèche.
                  </p>
                )}
              </div>
            )}

            {/* ── Résultat / refus ────────────────────────────────────────── */}
            {e.statut === 'refuse' && e.refusCommentaire && (
              <p className="mt-1 text-xs text-danger">Refusé : {e.refusCommentaire}</p>
            )}
            {(e.statut === 'envoye' || e.statut === 'echec') && e.resultat && (
              <ul className="mt-1 space-y-0.5">
                {e.resultat.map((r, i) => (
                  <li key={i} className="text-xs">
                    <span className={r.ok ? 'text-success' : 'text-danger'}>{r.ok ? '✓' : '✕'}</span>{' '}
                    <span className="text-ink">{r.label}</span>
                    {!r.ok && r.raison && <span className="text-ink-muted"> — {r.raison}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
